import { useState, useEffect, useRef } from 'react'
import MappingHeader from './header/MappingHeader'
import ProductSelectionPanel from './product-selection-panel/ProductSelectionPanel'
import VariantMappingPanel from './variant-mapping-panel/VariantMappingPanel'
import { searchVariants } from '../../services/VariantServices'
import { getProductById, productMapCatalog } from '../../services/ProductServices'
import { searchCatalogProductsPresetV2 } from '../../services/ProductLinesServices'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import MappedVariantContainer from './mapped-variant/MappedVariantContainer'
import { createArtworksForVariant, getVariantArtworks } from '../../services/ArtworkTemplateServices'
import SelectVariantsModal from './select-variant-modal/SelectVariantsModal'
import MapAllRemainingModal from './map-all-remaining-modal/MapAllRemainingModal'
import { getCurrentStore } from '../../services/CurrentStoreServices'
import { Spinner } from 'reactstrap'
import { toaster } from '../../shared/PFToast'
import { cloneDeep, get } from 'lodash'

const TEN_SECONDS = 10000;
const MappingContainer = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { productId } = useParams()
    const { variantId: urlVariantId } = useParams()

    const size = searchParams.get('size') || 20
    const page = searchParams.get('page') || 1
    const [variantId, setVariantId] = useState(urlVariantId)
    const [originalVariants, setOriginalVariants] = useState([]) // Store unfiltered list
    const [product, setProduct] = useState(null)
    const [catalogProducts, setCatalogProducts] = useState([])
    const [selectedProduct, setSelectedProduct] = useState({})
    const [listVariants, setListVariants] = useState([])
    const [selectedVariant, setSelectedVariant] = useState(null)
    const [currentPage, setCurrentPage] = useState(page)
    const [pageSize, setPageSize] = useState(size)
    const [totalPages, setTotalPages] = useState(0)
    const [isOpen, setIsOpen] = useState(false)
    const [variantFilter, setVariantFilter] = useState({}) // options map per attribute
    const [savedVariantFilters, setSavedVariantFilters] = useState({}) // persisted selections
    const [productArtWorks, setProductArtWorks] = useState(null)
    const [variantArtworks, setVariantArtworks] = useState([])
    const [remapVariantId, setRemapVariantId] = useState(null)
    const [activeStep, setActiveStep] = useState(1)
    const [catalogFilters, setCatalogFilters] = useState({
        productType: 'all',
        category: '',
        search: '',
    })
    const [remapping, setRemapping] = useState(false)
    const [searching, setSearching] = useState(false)
    const searchKey = useRef('-');
    const [mapAllModalOpen, setMapAllModalOpen] = useState(false)
    const [shouldAutoOpenModal, setShouldAutoOpenModal] = useState(false)
    const [isApplyingMapping, setIsApplyingMapping] = useState(false)

    const handleBack = () => {
        const redirect = `${process.env.REACT_APP_BASE_URL || 'https://seller-staging.merchize.com'
            }/a/products/${productId}`
        window.open(redirect, '_self')
    }

    const handleProductSelect = (product) => {
        setSelectedProduct(product)
        setIsOpen(true)
    }

    const handleCloseModal = () => {
        setIsOpen(false)
        setSelectedProduct({})
    }

    const searchCatalogProducts = async (filters) => {
        const store = getCurrentStore()
        if (!store?.length) {
            toaster({
                type: 'error',
                message: 'Store not found',
            })
            return
        }
        const payload = {
            allow_search_sku: true,
            category: catalogFilters.category,
            limit: pageSize,
            page: currentPage,
            sort: { popularity: -1 },
            status: 'active',
            store_slug: getCurrentStore(),
            title: catalogFilters.search,
            allow_combine_create_product_flow: true,
            ...filters,
        }
        const newSearchKey = _generateSearchKey(payload)
        const now = Date.now()
        const [sk, timestampStr] = searchKey.current.split('-');
        const timeStamp = parseInt(timestampStr, 10) || 0;
        if ((newSearchKey === sk) && (now - timeStamp < TEN_SECONDS)) {
            return
        }
        searchKey.current = `${newSearchKey}-${now}`;
        setSearching(true)
        try {
            const resp = await searchCatalogProductsPresetV2(payload)
            setCatalogProducts(resp.data.presets)
            setTotalPages(resp.data.pages)
        } catch (error) {
            toaster({
                type: 'error',
                message: error?.message,
            })
        } finally {
            setSearching(false)
        }
    }

    const handleVariantSelect = async (variant) => {
        setVariantId(variant?._id)
        setSelectedVariant(variant)
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev)
            newParams.set('variantId', variant._id)
            return newParams
        })
        if (variant.isMapped) {
            setVariantArtworks(productArtWorks?.[variant._id] || [])
            setActiveStep(4)
        } else {
            setActiveStep(1)
            const payload = {}
            if (catalogFilters.productType && catalogFilters.productType !== 'all') {
                payload.integration = catalogFilters.productType
            }
            await searchCatalogProducts(payload)
        }
    }

    const handleFilterClick = (filters) => {
        if (filters && typeof filters === 'object') {
            setSavedVariantFilters(filters)
            const filtered = originalVariants.filter((variant) => {
                return Object.entries(filters).every(([key, value]) => {
                    // Skip "All" values in filter
                    if (value === 'All') return true

                    return variant.options.some((option) => option.attribute.name === key && (option.name === value || option.slug === value))
                })
            })
            setListVariants(filtered)
            if (!filtered.find((variant) => variant._id === selectedVariant._id)) {
                setSelectedVariant(filtered[0])
            }
        }
    }

    useEffect(() => {
        if (!product) return
        searchCatalogProducts({})
        getVariantArtworks(productId).then((res) => {
            const productArtworks = res?.data || {}
            setProductArtWorks(productArtworks)
            if (variantId && !variantArtworks?.length && productArtworks?.[variantId]) {
                setVariantArtworks(productArtworks[variantId])
            }
        })
        searchVariants(productId, [], { limit: 1000 }).then((resp) => {
            if (!resp.success || !resp.data || !resp.data.map_catalog_variants.length) {
                throw new Error('Cannot fetch variants')
            }
            const variants = resp.data.map_catalog_variants
            const _filteredVariants = {}
            variants.forEach((variant) => {
                variant.isMapped = variant.map_catalog_confirmed
                variant.options.forEach((option) => {
                    if (!_filteredVariants[option.attribute.name]) {
                        _filteredVariants[option.attribute.name] = new Set()
                    }
                    _filteredVariants[option.attribute.name].add(option.name || option.slug)
                })
            })
            setOriginalVariants(variants) // Store original list
            setListVariants(variants)
            setVariantFilter(_filteredVariants)

            // Save variants to sessionStorage for reuse in other components
            sessionStorage.setItem(`variants_${productId}`, JSON.stringify({
                data: variants,
                timestamp: Date.now()
            }))

            // Check for auto-open modal condition
            const priorityVariant = findPriorityMappedVariant(variants)
            const unmappedVariants = variants.filter(v => !v.isMapped)
            const mappedVariants = variants.filter(v => v.isMapped)
            // Only auto-open if there's a valid priority variant, unmapped variants exist, and exactly 1 variant is mapped
            if (priorityVariant && unmappedVariants.length >= 1 && mappedVariants.length === 1) {
                setShouldAutoOpenModal(true)
            }

            let selectedVariant
            if (variantId) {
                selectedVariant = variants.find((v) => v._id === variantId)
            }
            if (!selectedVariant) {
                selectedVariant = variants.find((v) => !v.isMapped) || variants[0]
            }
            setSelectedVariant(selectedVariant)
            if (!variantId) {
                setSearchParams((prev) => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set('variantId', selectedVariant?._id)
                    return newParams
                })
            }
            handleVariantSelect(selectedVariant)
        }).catch((err) => {
            toaster({
                type: 'error',
                message: err.message,
            })
        })
    }, [product])

    const isShopifyProduct = (productData) => {
        const syncStatuses = get(productData, 'shopify_sync_statuses', [])
        const hasSyncStatus = Array.isArray(syncStatuses)
            ? syncStatuses.some((item) => item?.status === 'synced' || item?.status === 'need-update')
            : false
        return get(productData, 'external_product_id.source', '') === 'shopify' || hasSyncStatus
    }

    useEffect(() => {
        getProductById(productId).then((res) => {
            if (isShopifyProduct(res.data)) {
                setCatalogFilters(prev => ({ ...prev, productType: 'shopify' }))
            }
            setVariantId(searchParams.get('variantId'))
            setProduct(res.data)
        })
    }, [productId])

    useEffect(() => {
        if (variantId && originalVariants.length > 0 && productArtWorks) {
            const selectedVariant = originalVariants.find((v) => v._id === variantId)
            handleVariantSelect(selectedVariant)
        }
    }, [productArtWorks, originalVariants])

    useEffect(() => {
        if (selectedVariant && catalogFilters.productType === 'shopify' && !selectedVariant.isMapped) {
            handleVariantSelect(selectedVariant)
        }
    }, [catalogFilters.productType])


    useEffect(() => {
        if (!variantId) return
        const findVariant = originalVariants.find((v) => v._id === variantId)
        if (!findVariant) return
        setSelectedVariant(findVariant)
        if (isShopifyProduct(product)) {
            setCatalogFilters(prev => ({ ...prev, productType: 'shopify' }))
        }
        handleVariantSelect(findVariant)
    }, [variantId])

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage)
        const payload = {
            page: newPage,
        }
        if (catalogFilters.productType && catalogFilters.productType !== 'all') {
            payload.integration = catalogFilters.productType
        }
        searchCatalogProducts(payload)
    }

    const handleFilterChange = (data) => {
        setCurrentPage(1)
        const payload = {
            page: 1,
            limit: pageSize,
            category: data.category,
            title: data.search,
        }
        if (data.productType && data.productType !== 'all') {
            payload.integration = data.productType
        }
        searchCatalogProducts(payload)
    }

    const handleRemap = async () => {
        setRemapping(true)
        if (!selectedVariant) return;
        setRemapVariantId(selectedVariant._id)
        await Promise.all([
            await productMapCatalog(productId, {
                variant_catalog_maps: [{ variant_id: selectedVariant._id, sku: null }],
            }),
            await createArtworksForVariant(productId, {
                ids: [selectedVariant._id],
                sideGroups: [],
            }),
        ])

        await searchVariants(productId, [], { limit: 1000 }).then((resp) => {
            const variants = resp.data.map_catalog_variants;
            variants.forEach((variant) => {
                variant.isMapped = variant.map_catalog_confirmed;
            })
            setOriginalVariants(variants);

            // Update sessionStorage
            sessionStorage.setItem(`variants_${productId}`, JSON.stringify({
                data: variants,
                timestamp: Date.now()
            }))

            const filtered = variants.filter((variant) => {
                return Object.entries(savedVariantFilters).every(([key, value]) => {
                    if (value === 'All') return true;
                    return variant.options.some(
                        (option) => option.attribute.name === key && (option.name === value || option.slug === value)
                    )
                });
            });
            setListVariants(filtered);
            handleVariantSelect(variants.find((v) => v._id === selectedVariant._id))
        })
        setRemapping(false)
    }

    const handleAddMoreDesign = () => {
        const catalogProductId = selectedVariant.variant_catalog.product._id
        navigate(
            `/${catalogProductId}?variantId=${selectedVariant._id}&mappingProductId=${productId}&sku=${selectedVariant.sku}`
        )
    }

    const handleRemoveArtwork = async (artwork) => {
        const newVariantArtworks = variantArtworks.filter((art) => art.side_group !== artwork.side_group)
        const res = await createArtworksForVariant(productId, {
            ids: [selectedVariant._id],
            sideGroups: newVariantArtworks
        })
        if (res.success) {
            const prodAws = cloneDeep(productArtWorks);
            prodAws[selectedVariant._id] = newVariantArtworks;
            setProductArtWorks(prodAws);
            setVariantArtworks(newVariantArtworks);
            toaster({
                type: 'success',
                message: 'Remove artwork variant successfully',
            })
        } else {
            toaster({
                type: 'error',
                message: res.message,
            })
        }
    }

    const handleApplyForChange = async (value, artwork) => {
        const newVariantArtworks = variantArtworks.map((art) => {
            if (art.side_group === artwork.side_group) {
                return { ...art, pieces_per_pack: Number(value) }
            }
            return art
        })
        const res = await createArtworksForVariant(productId, {
            ids: [selectedVariant._id],
            sideGroups: newVariantArtworks
        })
        if (res.success) {
            const artworksResp = await getVariantArtworks(productId)
            const productArtworks = artworksResp?.data || {}
            setProductArtWorks(productArtworks)
            setVariantArtworks(productArtworks[selectedVariant._id] || [])
            toaster({
                type: 'success',
                message: 'Apply for variant successfully',
            })
        } else {
            toaster({
                type: 'error',
                message: res.message,
            })
        }
    }

    const onPageSizeChange = (newPageSize) => {
        const pageSizeInt = parseInt(newPageSize, 10)
        setPageSize(pageSizeInt)
        setCurrentPage(1)
        const payload = {
            limit: pageSizeInt,
            page: 1,
        }
        if (catalogFilters.productType && catalogFilters.productType !== 'all') {
            payload.integration = catalogFilters.productType
        }
        searchCatalogProducts(payload)
    }

    const _generateSearchKey = (payload) => {
        // Generate a unique key based on the search payload, to avoid redundant searches
        const keys = [
            "category",
            "title",
            "integration",
            "limit",
            "page",
        ]

        return keys.map((key) => `${payload[key] ?? ''}`).join('|')
    }

    const findPriorityMappedVariant = (variants) => {
        // Filter mapped variants
        const mappedShopifyVariants = variants.filter(v =>
            v.isMapped
        )

        if (mappedShopifyVariants.length === 0) return null

        // Priority 1: is_first_mapped
        const firstMappedShopify = mappedShopifyVariants.find(v => v.is_first_mapped)
        if (firstMappedShopify) return firstMappedShopify

        // Priority 2: is_default
        const defaultMappedShopify = mappedShopifyVariants.find(v => v.is_default)
        if (defaultMappedShopify) return defaultMappedShopify

        // Priority 3: Any variant
        return mappedShopifyVariants[0]
    }

    // Auto-open modal when conditions are met
    useEffect(() => {
        if (shouldAutoOpenModal && !mapAllModalOpen) {
            setMapAllModalOpen(true)
            setShouldAutoOpenModal(false)
        }
    }, [shouldAutoOpenModal, mapAllModalOpen])

    const handleOpenMapAllModal = () => {
        setMapAllModalOpen(true)
    }

    const handleCloseMapAllModal = () => {
        setMapAllModalOpen(false)
    }

    const handleApplyToAllVariants = async (selectedVariantIds, mappedVariantId, userMappings) => {
        setIsApplyingMapping(true)
        try {
            // Get template variant info for artworks
            const templateVariant = originalVariants.find(v => v._id === mappedVariantId)
            if (!templateVariant) {
                toaster({
                    type: 'error',
                    message: 'Template variant not found',
                })
                return
            }

            const artworks = productArtWorks?.[mappedVariantId] || []

            // Use userMappings directly as payload for mapping variants
            // userMappings already contains the correct SKU for each variant
            // Format: [{ variant_id: "xxx", sku: "KPPSVN000000BA00" }, ...]
            const variantCatalogMaps = userMappings

            // Call API to map catalog
            const res = await productMapCatalog(productId, {
                variant_catalog_maps: variantCatalogMaps,
                apply_artwork_variant_id: mappedVariantId
            })
            if (!res.success) {
                throw new Error(res.message || 'Failed to map catalog variants. Please try again.')
            }

            // Refresh variants list
            const resp = await searchVariants(productId, [], { limit: 1000 })
            const variants = resp.data.map_catalog_variants
            variants.forEach((variant) => {
                variant.isMapped = variant.map_catalog_confirmed
            })
            setOriginalVariants(variants)

            // Update sessionStorage
            sessionStorage.setItem(`variants_${productId}`, JSON.stringify({
                data: variants,
                timestamp: Date.now()
            }))

            const filtered = variants.filter((variant) => {
                return Object.entries(savedVariantFilters).every(([key, value]) => {
                    if (value === 'All') return true
                    return variant.options.some(
                        (option) => option.attribute.name === key && (option.name === value || option.slug === value)
                    )
                })
            })
            setListVariants(filtered)

            // Update product artworks
            const artworksResp = await getVariantArtworks(productId)
            setProductArtWorks(artworksResp?.data || {})

            // Close modal and show success message
            setMapAllModalOpen(false)
            toaster({
                type: 'success',
                message: `Successfully mapped ${selectedVariantIds.length} variants`
            })
        } catch (error) {
            toaster({
                type: 'error',
                message: error?.message || 'Failed to map variants'
            })
        } finally {
            setIsApplyingMapping(false)
        }
    }

    // Calculate if there's a valid priority variant for panel/modal display
    const priorityMappedVariant = findPriorityMappedVariant(originalVariants)
    const hasPriorityVariant = !!priorityMappedVariant

    return (
        <div className="ShopifyMappingContainer">
            <MappingHeader onBack={handleBack} activeStep={activeStep} />

            {/* {isLoading && <Progress style={{ height: 5 }} animated value={100} />} */}

            <main className="mapping-content">
                <div className="content-title">
                    <h2>{product?.title || ''}</h2>
                </div>

                <div className="content-body">
                    <VariantMappingPanel
                        variants={listVariants}
                        selectedVariantId={selectedVariant?._id}
                        mappedCount={listVariants.filter((v) => v.map_catalog_confirmed).length}
                        totalCount={listVariants.length}
                        onVariantSelect={handleVariantSelect}
                        onFilterClick={handleFilterClick}
                        variantFilter={variantFilter}
                        selectedFilters={savedVariantFilters}
                    />
                    {selectedVariant && !selectedVariant.isMapped && (
                        <ProductSelectionPanel
                            products={catalogProducts}
                            variants={originalVariants}
                            onOpenMapAllModal={handleOpenMapAllModal}
                            hasPriorityVariant={hasPriorityVariant}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            filters={catalogFilters}
                            setFilters={setCatalogFilters}
                            onPageChange={handlePageChange}
                            onProductSelect={handleProductSelect}
                            onFilterChange={handleFilterChange}
                            pageSize={pageSize}
                            onPageSizeChange={onPageSizeChange}
                            searching={searching}
                        />
                    )}
                    {selectedVariant &&
                        selectedVariant.isMapped &&
                        (remapping ? (
                            <Spinner style={{ color: '#007BFF' }} />
                        ) : (
                            <MappedVariantContainer
                                product={{
                                    name: selectedVariant.variant_catalog?.product?.title,
                                    sku: selectedVariant.variant_catalog?.sku,
                                    options: selectedVariant.variant_catalog?.option,
                                    image: selectedVariant.variant_catalog?.product?.thumbnail_link,
                                }}
                                artworks={variantArtworks}
                                onRemap={handleRemap}
                                onAddMoreDesign={handleAddMoreDesign}
                                onRemoveArtwork={handleRemoveArtwork}
                                onApplyForChange={handleApplyForChange}
                                variants={originalVariants}
                                onOpenMapAllModal={handleOpenMapAllModal}
                                hasPriorityVariant={hasPriorityVariant}
                            />
                        ))}
                </div>
            </main>

            <SelectVariantsModal
                isOpen={selectedProduct && isOpen}
                onClose={handleCloseModal}
                catalogProduct={selectedProduct}
                product={product}
                variantId={variantId}
                isRemapFlow={selectedVariant?._id === remapVariantId}
            />

            <MapAllRemainingModal
                product={product}
                productId={productId}
                isOpen={mapAllModalOpen}
                onClose={handleCloseMapAllModal}
                mappedVariant={findPriorityMappedVariant(originalVariants)}
                unmappedVariants={originalVariants.filter(v => !v.isMapped)}
                onApply={handleApplyToAllVariants}
                productArtworks={productArtWorks || {}}
                isLoading={isApplyingMapping}
            />
        </div>
    )
}

export default MappingContainer



